// @flow
import React, { Fragment, useState, useCallback, useEffect } from "react";
import moment from "moment";
import classNames from "classnames";
import PieChart from "./PieChart";
import _ from "underscore";
import FailureTds, { colSpan } from "./FailureTds";
import ProductRowInformation from "./ProductRowInformation.js";

function Toggler({ product, children }) {
  const [isActive, setToggle] = useState(false);
  const toggle = () => setToggle(!isActive);
  return children({ toggle, isActive });
}

export default function ProductPerCompoundFailureReport({ data, searchParams }) {
  const { measurements, products, totals, totals_percentage } = data;

  if (measurements.length == 0)
    return (
      <div style={{ marginTop: 20 }} className="basic-panel with-padding">
        There is no data matching your criteria.
      </div>
    );

  const measurementIndex = _.indexBy(measurements, "id");
  const getMeasurementName = id => measurementIndex[id].name;
  const measurement_ids = measurements.map(m => m.id);

  const renderTotalsRow = ({ title, data, round = false }) => {
    return (
      <Toggler>
        {({ toggle, isActive }) => (
          <tr
            className={classNames("clickable", "total_row", "top", { selected: isActive })}
            onClick={toggle}
          >
            <td className={classNames("row-title")}>{title}</td>
            {data.map(
              d =>
                isActive ? (
                  <td colSpan={colSpan} className={classNames("left_data", "graph")}>
                    {d.total > 0 && (
                      <PieChart pass={d.pass} amber={d.amber} fail={d.fail} title={d.title} />
                    )}
                  </td>
                ) : (
                  <FailureTds
                    key={d.measurement_id}
                    {...d}
                    title={getMeasurementName(d.measurement_id)}
                    round={round}
                  />
                )
            )}
          </tr>
        )}
      </Toggler>
    );
  };

  return (
    <div className="micro_summary_report">
      <table
        className={classNames(
          "table",
          "table-condensed",
          "table-hover",
          "micro_summary",
          "basic-panel",
          "with-shadow"
        )}
        style={{
          maxWidth: Math.max(measurements.length * 150 + 480, 1920)
        }}
      >
        <thead>
          <tr>
            <th className="borderless" rowSpan={2}>
              {products.length} Products &nbsp;&nbsp;-&nbsp;&nbsp;
              {measurements.length} Tests
              <br />
              <span className="instructions">Click on the product name to view charts</span>
            </th>
            {measurements.map(m => (
              <th className="measurement" colSpan={colSpan} key={m.id}>
                {m.name}
              </th>
            ))}
          </tr>
          <tr className="count_headings">
            {measurements.map(m => (
              <Fragment key={m.id}>
                <th className={classNames("left_data")}>Pass</th>
                <th>Amber</th>
                <th>Fail</th>
                <th className={classNames("right_data")}>Total</th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {renderTotalsRow({ title: "Total", data: totals })}
          {renderTotalsRow({
            title: "Percentage of Total",
            data: totals_percentage,
            round: true
          })}
          {products.map(product => (
            <Toggler key={product.id}>
              {({ toggle, isActive }) => (
                <Fragment>
                  <tr
                    className={classNames("clickable", { selected: isActive })}
                    onClick={toggle}
                  >
                    <td className={classNames("product_name")}>{product.name}</td>
                    {product.data.map(d => (
                      <FailureTds
                        key={d.measurement_id}
                        {...d}
                        title={product.name + " | " + getMeasurementName(d.measurement_id)}
                      />
                    ))}
                  </tr>
                  {isActive && (
                    <tr className="product_row_information">
                      <td colSpan={1 + product.data.length * colSpan}>
                        <ProductRowInformation
                          product={product}
                          measurement_ids={measurement_ids}
                          searchParams={searchParams}
                          getMeasurementName={getMeasurementName}
                          onClose={toggle}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )}
            </Toggler>
          ))}
        </tbody>
      </table>
    </div>
  );
}
